import React, { useMemo, useState, useEffect, useCallback } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Replace, Sparkles, ScanText } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";

function extractPlaceholders(text) {
  if (!text) return [];
  const matches = text.match(/\[[^\[\]]{2,60}\]/g) || [];
  return [...new Set(matches)];
}

function findContext(fullText, placeholder) {
  const idx = fullText.indexOf(placeholder);
  if (idx < 0) return "";
  const before = fullText.substring(Math.max(0, idx - 80), idx);
  const after = fullText.substring(idx + placeholder.length, Math.min(fullText.length, idx + placeholder.length + 80));
  const trimBefore = before.includes(" ") ? before.substring(before.indexOf(" ") + 1) : before;
  const trimAfter = after.includes(" ") ? after.substring(0, after.lastIndexOf(" ")) : after;
  const clean = (s) => s.replace(/\n+/g, " ").replace(/#{1,3}\s*/g, "").replace(/---/g, "").trim();
  return `...${clean(trimBefore)} ▸HERE◂ ${clean(trimAfter)}...`;
}

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
}

function fmtValue(val, ccy) {
  if (!val) return "";
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy || "GBP", minimumFractionDigits: 0 }).format(parseFloat(val)); }
  catch { return `${ccy || ""} ${Number(val).toLocaleString()}`; }
}

function buildAutoMap(formData) {
  const party = formData.contracting_party || {};
  const map = {};
  const add = (patterns, value) => { if (!value) return; for (const p of patterns) map[p] = value; };

  add(["insert date", "effective date", "commencement date", "start date", "date"], fmtDate(formData.effective_date));
  add(["expiry date", "expiration date", "end date", "termination date"], fmtDate(formData.expiration_date));
  add(["SERVICE PROVIDER LEGAL NAME", "service provider legal name", "service provider name",
    "supplier legal name", "supplier name", "vendor legal name", "vendor name",
    "contractor legal name", "contractor name", "provider legal name", "provider name",
    "counterparty legal name", "counterparty name"], party.legal_name);
  add(["service provider address", "service provider's address", "supplier address", "supplier's address",
    "vendor address", "contractor address"], party.registered_address);
  add(["CLIENT LEGAL NAME", "client legal name", "client name", "buyer legal name", "buyer name",
    "company legal name", "company name"], formData.contract_owner_name || "");
  add(["supplier email", "supplier's email", "provider email"], party.contact_email);
  add(["supplier phone", "supplier's phone", "provider phone"], party.contact_phone);
  add(["contract value", "total value", "agreement value"], fmtValue(formData.contract_value, formData.currency));
  if (party.registered_address) map["address"] = party.registered_address;
  if (formData.jurisdiction) {
    add(["State or Territory", "state or territory", "governing law", "jurisdiction"], formData.jurisdiction);
  }
  return map;
}

export function PlaceholderReviewPanel({ formData, setFormData, onApply }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [autoMatched, setAutoMatched] = useState({});

  const fullText = useMemo(() => [
    formData.content || "",
    ...(formData.sections || []).map((s) => s.content || ""),
  ].join("\n"), [formData.content, formData.sections]);

  const placeholders = useMemo(() => extractPlaceholders(fullText), [fullText]);

  const contexts = useMemo(() => {
    const map = {};
    for (const ph of placeholders) map[ph] = findContext(fullText, ph);
    return map;
  }, [placeholders, fullText]);

  const autoPopulate = useCallback(() => {
    const autoMap = buildAutoMap(formData);
    const newValues = {};
    const matched = {};
    for (const ph of placeholders) {
      const label = ph.slice(1, -1);
      if (autoMap[label]) { newValues[ph] = autoMap[label]; matched[ph] = true; }
      else { const lower = label.toLowerCase(); if (autoMap[lower]) { newValues[ph] = autoMap[lower]; matched[ph] = true; } }
    }
    if (Object.keys(newValues).length > 0) {
      setValues((prev) => ({ ...newValues, ...prev }));
      setAutoMatched(matched);
    }
  }, [formData, placeholders]);

  // Auto-populate and expand on load
  useEffect(() => {
    if (placeholders.length > 0) {
      autoPopulate();
      setOpen(true);
    } else {
      // No placeholders — nothing to do
    }
  }, [placeholders.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const filledCount = Object.entries(values).filter(([k, v]) => v?.trim() && placeholders.includes(k)).length;
  const autoCount = Object.keys(autoMatched).filter((k) => placeholders.includes(k)).length;
  const totalCount = placeholders.length;
  const hasScope = Boolean(formData.contract_scope);

  // Nothing at all
  if (totalCount === 0 && !hasScope) return null;
  // Only scope, no placeholders
  if (totalCount === 0) {
    return (
      <Card className="border-slate-200 bg-slate-50/30" data-testid="placeholder-review-panel">
        <CardContent className="py-4 space-y-2">
          <div className="flex items-center gap-1.5">
            <ScanText size={13} className="text-slate-400" />
            <span className="text-xs font-medium text-slate-600">Contract Scope</span>
            {hasScope && <Badge variant="secondary" className="text-[9px]">Defined</Badge>}
          </div>
          <Textarea
            value={formData.contract_scope || ""}
            onChange={(e) => setFormData((prev) => ({ ...prev, contract_scope: e.target.value }))}
            placeholder="Describe what this contract covers — services, obligations, boundaries, duration."
            rows={3}
            className="rounded-sm text-sm"
            data-testid="contract-scope-input"
          />
        </CardContent>
      </Card>
    );
  }

  const applyReplacements = () => {
    const entries = Object.entries(values).filter(([k, v]) => v?.trim() && placeholders.includes(k));
    if (entries.length === 0) return;
    const doReplace = (text) => {
      let result = text;
      for (const [placeholder, value] of entries) result = result.split(placeholder).join(value);
      return result;
    };
    setFormData((prev) => ({
      ...prev,
      content: doReplace(prev.content || ""),
      sections: (prev.sections || []).map((s) => ({ ...s, content: doReplace(s.content || "") })),
    }));
    setValues({});
    setAutoMatched({});
    setOpen(false);
    if (onApply) onApply();
  };

  return (
    <Card className="border-amber-200 bg-amber-50/30 dark:border-amber-800 dark:bg-amber-950/20" data-testid="placeholder-review-panel">
      <button
        className="w-full flex items-center justify-between px-5 py-3 text-left"
        onClick={() => setOpen(!open)}
        data-testid="placeholder-toggle"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-600" />
          <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
            {totalCount} placeholder{totalCount !== 1 ? "s" : ""} to complete
          </span>
          {autoCount > 0 && (
            <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200 text-[10px] gap-1">
              <Sparkles size={10} />{autoCount} auto-filled
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!open && filledCount > 0 && (
            <span className="text-[10px] text-emerald-600">{filledCount} ready to apply</span>
          )}
          {open ? <ChevronUp size={16} className="text-amber-500" /> : <ChevronDown size={16} className="text-amber-500" />}
        </div>
      </button>

      {open && (
        <CardContent className="pt-0 pb-4 space-y-4">
          {/* Scope */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <ScanText size={13} className="text-slate-400" />
              <span className="text-xs font-medium text-slate-600">Contract Scope</span>
              {hasScope && <Badge variant="secondary" className="text-[9px]">Defined</Badge>}
            </div>
            <Textarea
              value={formData.contract_scope || ""}
              onChange={(e) => setFormData((prev) => ({ ...prev, contract_scope: e.target.value }))}
              placeholder="Describe what this contract covers — services, obligations, boundaries, duration. This context helps AI reviews and guides clause generation."
              rows={3}
              className="rounded-sm text-sm"
              data-testid="contract-scope-input"
            />
          </div>

          {/* Placeholders */}
          <div className="border-t border-amber-200 dark:border-amber-800 pt-3">
            <p className="text-[11px] text-amber-700 dark:text-amber-400">
              {autoCount > 0
                ? `${autoCount} placeholder${autoCount !== 1 ? "s" : ""} auto-filled from contract details. Review, fill the rest, then Apply to reveal the contract content.`
                : "Fill in the values below and click Apply to reveal the contract content."
              }
            </p>
          </div>

          <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
            {placeholders.map((ph) => {
              const label = ph.slice(1, -1);
              const isFilled = values[ph]?.trim();
              const isAuto = autoMatched[ph];
              const ctx = contexts[ph];
              return (
                <div
                  key={ph}
                  className={`p-2.5 rounded-sm border transition-colors ${
                    isFilled
                      ? isAuto ? "border-cyan-200 bg-cyan-50/50" : "border-emerald-200 bg-emerald-50/50"
                      : "border-amber-200 bg-white dark:bg-slate-900"
                  }`}
                  data-testid={`placeholder-row-${label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="flex items-center gap-2">
                    {isFilled ? (
                      <CheckCircle2 size={14} className={isAuto ? "text-cyan-500 shrink-0" : "text-emerald-500 shrink-0"} />
                    ) : (
                      <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                    )}
                    <span className="text-xs font-mono text-slate-600 shrink-0 min-w-0" title={ph}>{ph}</span>
                    {isAuto && isFilled && <Badge variant="secondary" className="text-[9px] shrink-0">auto</Badge>}
                  </div>
                  {ctx && !isFilled && (
                    <p className="text-[10px] text-slate-400 mt-1 ml-6 leading-relaxed italic">
                      {ctx.split("▸HERE◂").map((part, i) =>
                        i === 0 ? <span key={i}>{part}</span> : <span key={i}><span className="font-semibold text-amber-600 not-italic bg-amber-100 px-0.5 rounded">{ph}</span>{part}</span>
                      )}
                    </p>
                  )}
                  <div className="mt-1.5 ml-6">
                    <Input
                      value={values[ph] || ""}
                      onChange={(e) => setValues((prev) => ({ ...prev, [ph]: e.target.value }))}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      className="rounded-sm h-8 text-sm"
                      data-testid={`placeholder-input-${label.replace(/\s+/g, "-").toLowerCase()}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-1">
            <p className="text-[10px] text-slate-400">
              {filledCount} of {totalCount} ready — unfilled placeholders stay as-is
            </p>
            <Button
              onClick={applyReplacements}
              disabled={filledCount === 0}
              size="sm"
              className="rounded-sm gap-1.5 bg-amber-600 hover:bg-amber-700"
              data-testid="apply-placeholders-btn"
            >
              <Replace size={14} />
              Apply & Show Content {filledCount > 0 ? `(${filledCount})` : ""}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
